/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { Schema, ValidationError } from '@squiz/component-editing-ui';
import {
  BUTTON_FORMAT_SOFT,
  BUTTON_THEME_DEFAULT,
  Button,
  ErrorMessage,
  SPINNER_TYPE_REGULAR,
  Spinner,
  TOAST_TYPE_SUCCESS,
  Toast,
  ToastProps,
  WizardStepProps,
} from '@squiz/sds';
import { useToastStore } from 'dxpConsoleCore-mf/State';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs';
import { JsonEditor } from '../../../../Components/JsonEditor/JsonEditor';
import { JsonEditorProps } from '../../../../Components/JsonEditor/JsonEditor.props';
import { CORE_STRINGS } from '../../../../Constants/Strings.constants';
import {
  TenantSettingsServicePutSettingDto,
  getTenantSettingsService,
  useSchema,
} from '../../../../Services/tenant-settings';
import { SETTING_LIST_DATA_IDS } from '../../SettingList.constants';
import { SettingValueFormProps } from './SettingValueForm.props';

/**
 * The form for configuring the value when creating a setting.
 */
export const SettingValueForm = (props: WizardStepProps): JSX.Element => {
  const { t } = useTranslation();
  const { close, setAfterClose, setButtonKeys, setButtons } = props;

  const settingCreateValueProps = props.customProps as SettingValueFormProps;
  const { clearCache, schemaId, serviceId, settingKey } = settingCreateValueProps;

  // Tenant Settings service
  const tenantSettingsService = getTenantSettingsService();

  const { updateActiveToast } = useToastStore();

  // Schema
  const [schema, schemaError] = useSchema(schemaId);

  const [formError, setFormError] = useState<string>(``);
  const [isFormSubmitted, setSubmit] = useState<boolean>(false);
  const [isBusy, setIsBusy] = useState<boolean>(false);

  // Form values
  const [settingValue, setSettingValue] = useState<unknown>();

  // Labels
  const labels = useMemo(() => {
    return {
      cancelButton: t(CORE_STRINGS.cancel),
      createButton: t(CORE_STRINGS.createSetting),
      errorUnknown: t(CORE_STRINGS.errorUnknown),
      errorUnknownContact: t(CORE_STRINGS.errorUnknownContact),
      settingCreated: t(CORE_STRINGS.settingCreated),
    };
  }, [t]);

  const submitHandler = useCallback(
    (errors: Array<ValidationError>): void => {
      setIsBusy(true);
      setFormError('');

      if (errors.length > 0) {
        setSubmit(false);
        setIsBusy(false);
        return;
      }

      const dto: TenantSettingsServicePutSettingDto = {
        schemaId,
        serviceId,
        settingKey,
        value: settingValue,
      };

      lastValueFrom(tenantSettingsService.putSetting(dto))
        .then(() => {
          const successToastProps: ToastProps = {
            showToast: true,
            stateHandler: useToastStore,
            title: labels.settingCreated,
            toastType: TOAST_TYPE_SUCCESS,
          };
          updateActiveToast(<Toast {...successToastProps} data-id={SETTING_LIST_DATA_IDS.settingCreatedToast} />);
          clearCache(new Date().toISOString());
          close();

          return true;
        })
        .catch(() => {
          setIsBusy(false);
          setSubmit(false);
          setFormError(labels.errorUnknown);
        });
    },
    [
      clearCache,
      close,
      labels,
      schemaId,
      serviceId,
      setFormError,
      setIsBusy,
      setSubmit,
      settingKey,
      settingValue,
      tenantSettingsService,
      updateActiveToast,
    ],
  );

  useEffect(() => {
    const cancelButton = (
      <Button
        data-id={SETTING_LIST_DATA_IDS.actionCreateSettingCancel}
        disabled={isBusy}
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => close()}
        text={labels.cancelButton}
        theme={BUTTON_THEME_DEFAULT}
      />
    );
    const createButton = (
      <Button
        data-id={SETTING_LIST_DATA_IDS.actionCreateSettingConfirm}
        disabled={isBusy}
        isLoading={isFormSubmitted}
        onClick={(): void => setSubmit(true)}
        text={labels.createButton}
      />
    );

    // Form submission check
    if (isFormSubmitted) {
      setButtonKeys([`create`]);
      setButtons([createButton]);
    } else if (schema) {
      setButtonKeys([`cancel`, `create`]);
      setButtons([cancelButton, createButton]);
    } else {
      setButtonKeys([]);
      setButtons([]);
    }
  }, [
    close,
    isBusy,
    isFormSubmitted,
    labels,
    schema,
    setAfterClose,
    setButtonKeys,
    setButtons,
    setSubmit,
    submitHandler,
  ]);

  if (schemaError) {
    return <ErrorMessage message={labels.errorUnknownContact} />;
  }

  if (!schema) {
    return (
      <div className="loading">
        <Spinner type={SPINNER_TYPE_REGULAR} />
      </div>
    );
  }

  // Setting value
  const settingValueProps: JsonEditorProps = {
    data: settingValue,
    onValidate: submitHandler,
    schema: schema as Schema,
    setData: setSettingValue,
    validate: isFormSubmitted,
  };

  return (
    <div className="service-settings__form" data-id={SETTING_LIST_DATA_IDS.settingValueForm}>
      <JsonEditor {...settingValueProps} />
      {formError && <ErrorMessage message={formError} />}
    </div>
  );
};
